/**
 * Registrar Factura x Mayor — migración web de ares9710 ("Facturar x Mayor").
 *
 * Factura la venta al por mayor de uno o varios vehículos (chasis) a un
 * cliente x mayor, al contado o a crédito con plan de cuotas mensuales.
 * En el servidor una factura genera una fila por cada chasis y todas las
 * filas comparten el mismo id de factura. El número de factura y el NCF
 * (si hay comprobantes configurados) los asigna el servidor al guardar;
 * el backend re-valida todos los datos antes de facturar.
 *
 * La factura a medio llenar se guarda sola en el navegador (borrador
 * "xmayor", ver lib/borradorventa) y se retoma al volver; los chasis se
 * revalidan al restaurar porque no quedan reservados.
 *
 * Multi-empresa: no se elige la empresa. Se ofrecen los chasis de todas y al
 * guardar el servidor emite una factura por cada empresa a la que pertenecen,
 * cada una con su NCF, su membrete y el inicial que se le asigne a mano.
 */
import React, { useState, useEffect, useMemo, useRef } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SaveIcon from "@mui/icons-material/Save";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert, useMaterialUIController } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";
import { precioEnMoneda, monedaLabel as etiquetaMoneda } from "lib/moneda";
import { imprimirFactura, etiquetaCuota } from "modules/ventasxmayor/impresionxmayor";
import { useBorradorVenta } from "lib/borradorventa";
import AvisoBorradorVenta from "components/borradorventa/AvisoBorradorVenta";

const filtrarClientes = createFilterOptions({
  stringify: (o) => (typeof o === "string" ? o : `${o.id} ${o.descrip || ""} ${o.rnc || ""}`),
  limit: 60,
});

const filtrarProductos = createFilterOptions({
  stringify: (o) =>
    typeof o === "string" ? o : `${o.chasis} ${o.marca || ""} ${o.modelo || ""} ${o.colore || ""}`,
  limit: 60,
});

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = parseFloat(String(v || "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const round2 = (n) => Math.round(n * 100) / 100;

const fmtFechaISO = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const hoyMasDias = (dias) => {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  return fmtFechaISO(d);
};

// Suma meses a una fecha "YYYY-MM-DD" ajustando al último día del mes destino.
const sumarMeses = (iso, meses) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  const dt = new Date(y, m - 1 + meses, 1);
  const ultimo = new Date(dt.getFullYear(), dt.getMonth() + 1, 0).getDate();
  dt.setDate(Math.min(d, ultimo));
  return fmtFechaISO(dt);
};

// Plan de cuotas de una factura: cuota 0 = inicial pactado (si lo hay) y las
// cuotas 1..n reparten total - inicial (mismo cálculo que el servidor).
const calcularPlan = ({ total, inicial, plazos, fechaprimerpago, vencimientoinicial }) => {
  const n = parseInt(plazos, 10) || 0;
  const financiado = round2(total - inicial);
  if (n < 1 || n > 60 || financiado <= 0 || !fechaprimerpago) return [];
  const cuota = round2(financiado / n);
  const filas = [];
  if (inicial > 0) filas.push({ numero: 0, fecha: vencimientoinicial, valor: inicial });
  for (let i = 0; i < n; i += 1) {
    filas.push({
      numero: i + 1,
      fecha: sumarMeses(fechaprimerpago, i),
      valor: i === n - 1 ? round2(financiado - cuota * (n - 1)) : cuota,
    });
  }
  return filas;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color, extra }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
      {extra}
    </Box>
  );
}

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Agregue los chasis a facturar
      </Typography>
    </Stack>
  );
}

// Comprobante de la factura (equivalente web de la impresión de ares9710).
// Delegado al formato carta de impresionxmayor para que la factura al
// facturar y su reimpresión salgan idénticas.
export function imprimirFacturaXmayor(r) {
  imprimirFactura({
    compania: r.compania,
    factura: {
      id: r.id,
      fecha: r.fecha,
      ncf: r.ncf,
      cliente: r.cliente,
      rnc: r.rnc,
      concepto: r.concepto,
      tipofactura: r.tipofactura,
      valor: r.total,
      monedaTexto: r.monedaLabel,
      creado: r.creado,
      inicialpactado: r.inicialpactado,
      inicialpagado: r.inicialcobrado,
      reciboinicial: r.reciboinicial,
    },
    lineas: (r.items || []).map((i) => ({ ...i, preciomotor: i.precio })),
    cuotas: (r.cuotas || []).map((c) => ({
      numero_pago: c.numero,
      fecha: c.fecha,
      valor: c.valor,
    })),
  });
}

export default function FacturarXmayor({
  embedded = false,
  onExit,
  onSaved,
  restaurarBorrador = false,
} = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const [{ usuarioLog }] = useMaterialUIController();

  const [config, setConfig] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [productos, setProductos] = useState([]);
  const [cargando, setCargando] = useState(true);

  const [cliente, setCliente] = useState(null);
  const [tipocomprobante, setTipocomprobante] = useState("");
  const [tipofactura, setTipofactura] = useState("Contado");
  const [moneda, setMoneda] = useState("P");
  const [tasa, setTasa] = useState("");
  const [consignado, setConsignado] = useState("N");
  const [concepto, setConcepto] = useState("");
  const [items, setItems] = useState([]);
  const [prodSel, setProdSel] = useState(null);
  const [plazos, setPlazos] = useState("1");
  const [fechaprimerpago, setFechaprimerpago] = useState(hoyMasDias(30));
  // Inicial pactado (solo crédito): cuota 0 con vencimiento hoy; las cuotas
  // reparten total - inicial. Lo cobrado ahora sale como recibo normal.
  const [inicialpactado, setInicialpactado] = useState("");
  const [inicialcobrado, setInicialcobrado] = useState("");
  const [tipopagoinicial, setTipopagoinicial] = useState("Contado");
  const [referenciainicial, setReferenciainicial] = useState("");
  // Vencimiento del completivo (lo que quede sin cobrar del inicial).
  const [vencimientoinicial, setVencimientoinicial] = useState(hoyMasDias(0));
  // Multi-empresa con chasis de varias empresas: inicial asignado a mano por
  // empresa, { [empresa]: { pactado, cobrado } } (texto digitado).
  const [inicialesEmpresa, setInicialesEmpresa] = useState({});
  const [validando, setValidando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  // Facturas emitidas en el último guardado cuando salió más de una (una por
  // empresa): se listan para imprimirlas una a una.
  const [emitidas, setEmitidas] = useState(null);

  const cargarProductos = async () => {
    const { ok, data } = await post("/api/xmayor_productos", {});
    if (ok) setProductos(data.productos || []);
  };

  // Devuelve { conf, clientes } para que el borrador se restaure sobre la
  // configuración ya cargada (ver restaurar).
  const cargar = async () => {
    setCargando(true);
    try {
      const [conf, cli, prod] = await Promise.all([
        post("/api/xmayor_config", {}),
        post("/api/xmayor_clientes", {}),
        post("/api/xmayor_productos", {}),
      ]);
      if (conf.ok) {
        setConfig(conf.data);
        setTipocomprobante(conf.data.tiposComprobante?.[0] || "");
        setTasa(String(conf.data.tasaus ?? ""));
      } else {
        showAlert({
          message: conf.data.Error || "No se pudo cargar la configuración",
          color: "error",
        });
      }
      if (cli.ok) setClientes(cli.data.clientes || []);
      if (prod.ok) setProductos(prod.data.productos || []);
      return {
        conf: conf.ok ? conf.data : null,
        clientes: cli.ok ? cli.data.clientes || [] : [],
        productos: prod.ok ? prod.data.productos || [] : [],
      };
    } finally {
      setCargando(false);
    }
  };

  const cargaRef = useRef(null);
  useEffect(() => {
    cargaRef.current = cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const usaMultiempresa = !!config?.multiempresa;
  const nombreEmpresa = (emp) => {
    const e = (config?.empresas || []).find((x) => String(x.empresa) === String(emp));
    return `${emp} — ${e?.nombre || `Empresa ${emp}`}`;
  };

  const mostrarConsignado = config?.usaconsignado === "S" && tipofactura === "Credito";
  const monedaLabel = etiquetaMoneda(moneda);
  // Moneda en la que están expresados producto.precio/mprecio y el límite
  // de crédito del cliente. Sin catálogo de monedas el backend responde
  // "DOP" y todo se comporta como siempre.
  const monedaBase = config?.base || "DOP";
  const labelBase = etiquetaMoneda(monedaBase);
  const tasaus = toNum(config?.tasaus);

  const disponibles = useMemo(
    () => productos.filter((p) => !items.some((i) => i.chasis === p.chasis)),
    [productos, items]
  );

  const total = useMemo(() => items.reduce((acc, i) => acc + toNum(i.precio), 0), [items]);

  // Un grupo por empresa de los chasis (una factura por grupo al guardar).
  // Sin multi-empresa hay un único grupo con todo.
  const grupos = useMemo(() => {
    if (!usaMultiempresa) return [{ empresa: null, items, total }];
    const mapa = new Map();
    items.forEach((i) => {
      const emp = Number(i.empresa) || 1;
      if (!mapa.has(emp)) mapa.set(emp, []);
      mapa.get(emp).push(i);
    });
    return [...mapa.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([emp, its]) => ({
        empresa: emp,
        items: its,
        total: its.reduce((acc, i) => acc + toNum(i.precio), 0),
      }));
  }, [usaMultiempresa, items, total]);
  const variasEmpresas = grupos.length > 1;
  // Empresas de la venta sin NCF configurado (la factura sale sin comprobante).
  const empresasSinNcf = usaMultiempresa
    ? grupos.filter((g) => config?.ncfempresas?.[String(g.empresa)] === false).map((g) => g.empresa)
    : [];
  let avisoNcf;
  if (usaMultiempresa) {
    if (empresasSinNcf.length > 0)
      avisoNcf = `NCF no configurado en la empresa ${empresasSinNcf.join(
        ", "
      )} — su factura saldrá sin comprobante fiscal`;
  } else if (config && !config.ncfdisponible) {
    avisoNcf = "NCF no configurado — la factura se emitirá sin comprobante fiscal";
  }

  // El precio facturado no puede quedar por debajo del mínimo del chasis
  // (producto.mprecio, en la moneda BASE): en facturas de otra moneda se
  // compara ya convertido, igual que el precio que se guarda. El backend
  // re-valida.
  const minimoEnMoneda = (i) => precioEnMoneda(i.mprecio, moneda, tasa, monedaBase);
  const bajoElMinimo = (i) => minimoEnMoneda(i) > 0 && toNum(i.precio) < minimoEnMoneda(i);
  const hayBajoMinimo = useMemo(() => items.some(bajoElMinimo), [items, moneda, tasa, monedaBase]);

  // Inicial pactado: solo a crédito y con el flag de configuración
  // (varinicial.inicialxmayor); apagado, la factura es la de siempre.
  const usaInicial = !!config?.inicialxmayor && tipofactura === "Credito";
  // Inicial de cada grupo: con varias empresas sale de lo digitado por
  // empresa; con una sola, de los campos de siempre.
  const inicialDe = (g) => {
    if (!usaInicial) return { pactado: 0, cobrado: 0 };
    if (!variasEmpresas) {
      return { pactado: round2(toNum(inicialpactado)), cobrado: round2(toNum(inicialcobrado)) };
    }
    const v = inicialesEmpresa[g.empresa] || {};
    return { pactado: round2(toNum(v.pactado)), cobrado: round2(toNum(v.cobrado)) };
  };
  const cambiarInicialEmpresa = (emp, campo, v) =>
    setInicialesEmpresa((prev) => ({ ...prev, [emp]: { ...(prev[emp] || {}), [campo]: v } }));

  const planes = useMemo(
    () =>
      grupos.map((g) => {
        const { pactado, cobrado } = inicialDe(g);
        return {
          ...g,
          inicial: pactado,
          cobrado,
          financiado: round2(g.total - pactado),
          cuotas:
            tipofactura === "Credito"
              ? calcularPlan({
                  total: g.total,
                  inicial: pactado,
                  plazos,
                  fechaprimerpago,
                  vencimientoinicial,
                })
              : [],
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      grupos,
      tipofactura,
      plazos,
      fechaprimerpago,
      vencimientoinicial,
      usaInicial,
      inicialpactado,
      inicialcobrado,
      inicialesEmpresa,
    ]
  );
  const inicial = round2(planes.reduce((acc, p) => acc + p.inicial, 0));
  const cobradoInicial = round2(planes.reduce((acc, p) => acc + p.cobrado, 0));
  const financiado = round2(total - inicial);
  const cuotasPreview = variasEmpresas ? [] : planes[0]?.cuotas || [];
  const cuotasPlan = cuotasPreview.filter((c) => c.numero !== 0);

  const limpiar = () => {
    setCliente(null);
    setTipocomprobante(config?.tiposComprobante?.[0] || "");
    setTipofactura("Contado");
    setMoneda("P");
    setTasa(String(config?.tasaus ?? ""));
    setConsignado("N");
    setConcepto("");
    setItems([]);
    setProdSel(null);
    setPlazos("1");
    setFechaprimerpago(hoyMasDias(30));
    setInicialpactado("");
    setInicialcobrado("");
    setInicialesEmpresa({});
    setTipopagoinicial("Contado");
    setReferenciainicial("");
    setVencimientoinicial(hoyMasDias(0));
  };

  const agregarChasis = async (prod) => {
    setProdSel(null);
    if (!prod) return;
    if (items.some((i) => i.chasis === prod.chasis)) {
      showAlert({ message: "El chasis ya está agregado a la factura", color: "warning" });
      return;
    }
    setValidando(true);
    try {
      const { ok, data } = await post("/api/xmayor_validarchasis", { chasis: prod.chasis });
      if (!ok) {
        showAlert({ message: data.Error || "El chasis no está disponible", color: "error" });
        return;
      }
      const precio = precioEnMoneda(prod.precio, moneda, tasa, monedaBase);
      setItems((prev) => [
        ...prev,
        {
          chasis: prod.chasis,
          marca: prod.marca,
          modelo: prod.modelo,
          colore: prod.colore,
          ano: prod.ano,
          // número de máquina (motor) para la factura impresa al facturar
          maquina: prod.maquina,
          // empresa dueña del chasis: decide en qué factura sale
          empresa: Number(prod.empresa ?? data.producto?.empresa) || 1,
          precio: precio.toFixed(2),
          // mínimo del chasis en la moneda BASE; se convierte al vuelo porque
          // la moneda y la tasa de la factura pueden cambiar tras agregarlo.
          mprecio: toNum(prod.mprecio),
        },
      ]);
    } finally {
      setValidando(false);
    }
  };

  const actualizarPrecio = (chasis, v) => {
    setItems((prev) => prev.map((i) => (i.chasis === chasis ? { ...i, precio: v } : i)));
  };

  const quitarChasis = (chasis) => {
    setItems((prev) => prev.filter((i) => i.chasis !== chasis));
  };

  const guardar = async () => {
    if (!cliente) {
      showAlert({ message: "Debe seleccionar el cliente", color: "warning" });
      return;
    }
    if (items.length === 0) {
      showAlert({ message: "Debe agregar al menos un chasis a la factura", color: "warning" });
      return;
    }
    const t = toNum(tasa);
    if (t <= 0) {
      showAlert({ message: "La tasa no puede estar en 0", color: "warning" });
      return;
    }
    if (tasaus > 0 && t < tasaus) {
      showAlert({
        message: `La tasa no puede ser menor que la mínima (${fmtMoney(tasaus)})`,
        color: "warning",
      });
      return;
    }
    if (!concepto.trim()) {
      showAlert({ message: "Debe indicar el concepto de la factura", color: "warning" });
      return;
    }
    const nPlazos = parseInt(plazos, 10) || 0;
    if (tipofactura === "Credito" && (nPlazos < 1 || nPlazos > 60)) {
      showAlert({ message: "Los plazos deben estar entre 1 y 60", color: "warning" });
      return;
    }
    if (tipofactura === "Credito") {
      const deEmpresa = (p) => (variasEmpresas ? ` (empresa ${nombreEmpresa(p.empresa)})` : "");
      const invalido = planes.find((p) => p.inicial < 0 || p.inicial >= p.total);
      if (invalido) {
        showAlert({
          message: `El inicial pactado debe ser menor que el total de la factura${deEmpresa(
            invalido
          )}`,
          color: "warning",
        });
        return;
      }
      if (inicial > 0 && (!vencimientoinicial || vencimientoinicial < hoyMasDias(0))) {
        showAlert({
          message: "La fecha de vencimiento del completivo no puede ser anterior a hoy",
          color: "warning",
        });
        return;
      }
      const excedido = planes.find((p) => p.cobrado < 0 || p.cobrado > p.inicial);
      if (excedido) {
        showAlert({
          message: `El inicial cobrado no puede ser mayor que el inicial pactado${deEmpresa(
            excedido
          )}`,
          color: "warning",
        });
        return;
      }
    }
    const bajoMinimo = items.find(bajoElMinimo);
    if (bajoMinimo) {
      showAlert({
        message: `El precio del chasis ${
          bajoMinimo.chasis
        } no puede ser menor al precio mínimo (${monedaLabel} ${fmtMoney(
          minimoEnMoneda(bajoMinimo)
        )})`,
        color: "warning",
      });
      return;
    }
    setGuardando(true);
    try {
      const { ok, data } = await post("/api/xmayor_facturar", {
        cliente: cliente.id,
        tipocomprobante,
        tipofactura,
        moneda,
        tasa: t,
        concepto: concepto.trim(),
        consignado: mostrarConsignado ? consignado : "N",
        items: items.map((i) => ({ chasis: i.chasis, precio: toNum(i.precio) })),
        plazos: tipofactura === "Credito" ? nPlazos : 1,
        fechaprimerpago,
        inicialpactado: variasEmpresas ? 0 : inicial,
        inicialcobrado: variasEmpresas ? 0 : cobradoInicial,
        // Varias empresas: el inicial va asignado a mano a cada factura.
        ...(variasEmpresas
          ? {
              iniciales: planes.map((p) => ({
                empresa: p.empresa,
                inicialpactado: p.inicial,
                inicialcobrado: p.cobrado,
              })),
            }
          : {}),
        tipopagoinicial,
        referenciainicial: referenciainicial.trim(),
        vencimientoinicial,
      });
      if (ok) {
        // Una factura por empresa; cada una lleva sus chasis para imprimirla.
        const fecha = new Date().toLocaleDateString("es-DO");
        const facturas = (data.facturas || [data]).map((f) => ({
          // Multi-empresa: el backend devuelve el membrete de la empresa de la factura.
          compania: f.compania || config?.compania,
          empresa: f.empresa,
          tipofactura,
          id: f.id,
          fecha,
          ncf: f.ncf,
          cliente: cliente.descrip,
          rnc: cliente.rnc,
          concepto,
          monedaLabel,
          items: f.chasis ? items.filter((i) => f.chasis.includes(i.chasis)) : items,
          total: f.total ?? total,
          cuotas: f.cuotas || [],
          creado: usuarioLog,
          inicialpactado: f.inicialpactado ?? inicial,
          inicialcobrado: f.inicialcobrado ?? cobradoInicial,
          reciboinicial: f.reciboinicial,
        }));
        if (facturas.length === 1) {
          const f = facturas[0];
          showAlert({
            message: f.reciboinicial
              ? `Factura #${f.id} registrada · inicial cobrado en el recibo #${f.reciboinicial}`
              : `Factura #${f.id} registrada`,
            color: "success",
          });
          imprimirFacturaXmayor(f);
        } else {
          showAlert({
            message: `${facturas.length} facturas registradas (${facturas
              .map((f) => `#${f.id}`)
              .join(", ")})`,
            color: "success",
          });
          setEmitidas(facturas);
        }
        limpiar();
        if (onSaved) onSaved();
        cargarProductos();
      } else {
        showAlert({ message: data.Error || "No se pudo registrar la factura", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  // ---- Borrador ("stand by", ver lib/borradorventa) ----
  // Los chasis no quedan reservados mientras la factura está en pausa: al
  // retomar se revalidan uno por uno y los que ya no están disponibles se
  // quitan (los precios digitados de los demás se conservan).
  const [restaurando, setRestaurando] = useState(false);
  const restaurar = async (d) => {
    setRestaurando(true);
    try {
      const carga = (await cargaRef.current) || {};
      const conf = carga.conf || {};
      const cli = d.cliente
        ? (carga.clientes || []).find((c) => String(c.id) === String(d.cliente.id)) || d.cliente
        : null;
      setCliente(cli);
      if (d.tipocomprobante && (conf.tiposComprobante || []).includes(d.tipocomprobante))
        setTipocomprobante(d.tipocomprobante);
      setTipofactura(d.tipofactura || "Contado");
      setMoneda(d.moneda || "P");
      setTasa(d.tasa ?? String(conf.tasaus ?? ""));
      setConsignado(d.consignado || "N");
      setConcepto(d.concepto || "");
      setPlazos(d.plazos || "1");
      setFechaprimerpago(d.fechaprimerpago || hoyMasDias(30));
      setInicialpactado(d.inicialpactado || "");
      setInicialcobrado(d.inicialcobrado || "");
      setInicialesEmpresa(d.inicialesEmpresa || {});
      setTipopagoinicial(d.tipopagoinicial || "Contado");
      setReferenciainicial(d.referenciainicial || "");
      setVencimientoinicial(d.vencimientoinicial || hoyMasDias(0));
      setProdSel(null);

      const guardados = d.items || [];
      const validaciones = await Promise.all(
        guardados.map((i) => post("/api/xmayor_validarchasis", { chasis: i.chasis }))
      );
      const fuera = guardados.filter((_, k) => !validaciones[k].ok);
      // La empresa se toma de la validación: los borradores viejos no la traen.
      setItems(
        guardados
          .map((i, k) => ({
            ...i,
            empresa: Number(validaciones[k].data?.producto?.empresa ?? i.empresa) || 1,
            // año y máquina no viajaban en los borradores viejos: salen del inventario
            ano: i.ano ?? (carga.productos || []).find((p) => p.chasis === i.chasis)?.ano,
            maquina:
              i.maquina ?? (carga.productos || []).find((p) => p.chasis === i.chasis)?.maquina,
          }))
          .filter((_, k) => validaciones[k].ok)
      );
      if (fuera.length) {
        showAlert({
          message: `${fuera.length === 1 ? "El chasis" : "Los chasis"} ${fuera
            .map((i) => i.chasis)
            .join(", ")} ya no ${
            fuera.length === 1 ? "está disponible y se quitó" : "están disponibles y se quitaron"
          } de la factura.`,
          color: "warning",
        });
      }
    } finally {
      setRestaurando(false);
    }
  };

  const borrador = useBorradorVenta({
    tipo: "xmayor",
    datos: {
      cliente: cliente
        ? {
            id: cliente.id,
            descrip: cliente.descrip,
            rnc: cliente.rnc,
            limcredito: cliente.limcredito,
          }
        : null,
      tipocomprobante,
      tipofactura,
      moneda,
      tasa,
      consignado,
      concepto,
      items,
      plazos,
      fechaprimerpago,
      inicialpactado,
      inicialcobrado,
      inicialesEmpresa,
      tipopagoinicial,
      referenciainicial,
      vencimientoinicial,
    },
    resumen: {
      cliente: cliente?.descrip || "",
      chasis: items.length === 1 ? items[0].chasis : "",
      detalle: items.length > 1 ? `${items.length} chasis` : "",
    },
    conDatos: !!cliente || items.length > 0 || !!concepto.trim(),
    restaurar,
    autoRestaurar: restaurarBorrador,
    pausado: cargando || restaurando,
  });

  const columns = [
    { field: "chasis", headerName: "Chasis", flex: 1, minWidth: 160 },
    { field: "marca", headerName: "Marca", width: 120 },
    { field: "modelo", headerName: "Modelo", width: 120 },
    { field: "colore", headerName: "Color", width: 110 },
    {
      field: "precio",
      headerName: "Precio",
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <TextField
          size="small"
          value={params.row.precio}
          onChange={(e) => actualizarPrecio(params.row.chasis, e.target.value)}
          error={bajoElMinimo(params.row)}
          title={
            minimoEnMoneda(params.row) > 0
              ? `Precio mínimo ${fmtMoney(minimoEnMoneda(params.row))}`
              : ""
          }
          inputProps={{ style: { textAlign: "right" } }}
          sx={fpInputSx}
        />
      ),
    },
    {
      field: "mprecio",
      headerName: "Mínimo",
      width: 120,
      align: "right",
      headerAlign: "right",
      renderCell: (params) =>
        minimoEnMoneda(params.row) > 0 ? fmtMoney(minimoEnMoneda(params.row)) : "—",
    },
    {
      field: "acciones",
      headerName: "",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={() => quitarChasis(params.row.chasis)} title="Quitar">
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const columnasCuotas = [
    {
      field: "numero",
      headerName: "Cuota",
      width: 90,
      valueFormatter: (value) => etiquetaCuota(value),
    },
    { field: "fecha", headerName: "Fecha", flex: 1, minWidth: 120 },
    {
      field: "valor",
      headerName: "Valor",
      width: 150,
      align: "right",
      headerAlign: "right",
      valueFormatter: (value) => fmtMoney(value),
    },
  ];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Registrar Factura x Mayor"
        subtitle="Factura vehículos al por mayor, al contado o a crédito"
        cta={
          <Stack direction="row" spacing={1.2}>
            {embedded && (
              <Button startIcon={<ArrowBackIcon />} onClick={onExit} sx={btnOutline(FP.color.navy)}>
                Volver
              </Button>
            )}
            <Button startIcon={<RestartAltIcon />} onClick={limpiar} sx={btnOutline(FP.color.navy)}>
              Nuevo
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <AvisoBorradorVenta
          borrador={borrador.oferta}
          etiqueta="factura x mayor"
          onContinuar={borrador.continuar}
          onDescartar={borrador.descartar}
          sx={{ mb: 2.4 }}
        />
        {restaurando && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <CircularProgress size={16} />
            <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary }}>
              Retomando la factura en pausa y validando los chasis...
            </Typography>
          </Stack>
        )}
        {/* Datos de la factura */}
        <Box sx={sectionBox}>
          <SectionTitle icon={ReceiptLongOutlinedIcon} color={FP.color.accentBlue}>
            Datos de la Factura
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarClientes}
                options={clientes}
                loading={cargando}
                value={cliente}
                getOptionLabel={(o) => (typeof o === "string" ? o : `${o.id} — ${o.descrip || ""}`)}
                isOptionEqualToValue={(o, v) => (o.id || o) === (v.id || v)}
                onChange={(_, v) => setCliente(v)}
                renderOption={(props, o) => (
                  <li {...props} key={o.id}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.id} — {o.descrip}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[o.rnc ? `RNC ${o.rnc}` : ""].filter(Boolean).join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Cliente" sx={fpInputSx} />}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center" }}>
              {cliente && toNum(cliente.limcredito) > 0 && (
                <Chip
                  label={`Límite de crédito: ${labelBase} ${fmtMoney(cliente.limcredito)}`}
                  sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                select
                fullWidth
                label="Tipo de Comprobante"
                value={tipocomprobante}
                onChange={(e) => setTipocomprobante(e.target.value)}
                disabled={cargando}
                helperText={avisoNcf}
                sx={fpInputSx}
              >
                {(config?.tiposComprobante || []).map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                select
                fullWidth
                label="Tipo de Factura"
                value={tipofactura}
                onChange={(e) => setTipofactura(e.target.value)}
                sx={fpInputSx}
              >
                {(config?.tiposFactura || ["Contado", "Credito"]).map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField
                select
                fullWidth
                label="Moneda"
                value={moneda}
                onChange={(e) => setMoneda(e.target.value)}
                disabled={items.length > 0}
                helperText={items.length > 0 ? "Bloqueada con chasis agregados" : undefined}
                sx={fpInputSx}
              >
                <MenuItem value="P">{etiquetaMoneda("P")}</MenuItem>
                <MenuItem value="U">USD</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <TextField
                fullWidth
                label="Tasa"
                value={tasa}
                onChange={(e) => setTasa(e.target.value)}
                placeholder="0.00"
                helperText={`Mínima: ${fmtMoney(tasaus)}`}
                inputProps={{ style: { textAlign: "right" } }}
                sx={fpInputSx}
              />
            </Grid>
            {mostrarConsignado && (
              <Grid item xs={6} sm={4} md={2}>
                <TextField
                  select
                  fullWidth
                  label="Tipo"
                  value={consignado}
                  onChange={(e) => setConsignado(e.target.value)}
                  sx={fpInputSx}
                >
                  <MenuItem value="N">Factura</MenuItem>
                  <MenuItem value="S">Consignado</MenuItem>
                </TextField>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Concepto"
                value={concepto}
                onChange={(e) => setConcepto(e.target.value)}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Chasis */}
        <Box sx={sectionBox}>
          <SectionTitle
            icon={DirectionsCarFilledOutlinedIcon}
            color={FP.color.success}
            extra={
              <Chip
                label={`Valor Total: ${monedaLabel} ${fmtMoney(total)}`}
                sx={{
                  ml: "auto",
                  bgcolor: FP.color.surfaceAlt,
                  color: FP.color.textSecondary,
                  fontWeight: 700,
                }}
              />
            }
          >
            Chasis
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                autoHighlight
                selectOnFocus
                handleHomeEndKeys
                filterOptions={filtrarProductos}
                options={disponibles}
                loading={cargando || validando}
                value={prodSel}
                getOptionLabel={(o) =>
                  typeof o === "string"
                    ? o
                    : `${o.chasis} — ${[o.marca, o.modelo].filter(Boolean).join(" ")}`
                }
                isOptionEqualToValue={(o, v) => (o.chasis || o) === (v.chasis || v)}
                onChange={(_, v) => agregarChasis(v)}
                renderOption={(props, o) => (
                  <li {...props} key={o.chasis}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {o.chasis}
                      </Typography>
                      <Typography sx={{ fontSize: "0.72rem", color: FP.color.textMuted }}>
                        {[
                          [o.marca, o.modelo, o.colore].filter(Boolean).join(" "),
                          `${monedaLabel} ${fmtMoney(
                            precioEnMoneda(o.precio, moneda, tasa, monedaBase)
                          )}`,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Agregar chasis"
                    helperText="Busque por chasis, marca o modelo"
                    sx={fpInputSx}
                  />
                )}
              />
            </Grid>
            {validando && (
              <Grid item xs={12} md={6} sx={{ display: "flex", alignItems: "center" }}>
                <CircularProgress size={18} />
              </Grid>
            )}
          </Grid>
          {variasEmpresas && (
            <Typography sx={{ mt: 1.6, fontSize: "0.82rem", color: FP.color.textSecondary }}>
              Los chasis son de {grupos.length} empresas: se emitirá una factura por cada una.
            </Typography>
          )}
          {(items.length === 0 ? [{ empresa: null, items: [], total: 0 }] : grupos).map((g) => (
            <Box key={g.empresa ?? "todos"} sx={{ mt: 2 }}>
              {usaMultiempresa && g.empresa != null && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 0.8, flexWrap: "wrap", rowGap: 0.6 }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: "0.85rem", color: FP.color.navy }}>
                    Empresa {nombreEmpresa(g.empresa)}
                  </Typography>
                  <Chip
                    size="small"
                    label={`${g.items.length} chasis · ${monedaLabel} ${fmtMoney(g.total)}`}
                    sx={{
                      bgcolor: FP.color.surfaceAlt,
                      color: FP.color.textSecondary,
                      fontWeight: 700,
                    }}
                  />
                </Stack>
              )}
              <Box sx={{ bgcolor: "#fff" }}>
                <DataGrid
                  autoHeight
                  rows={g.items}
                  columns={columns}
                  getRowId={(r) => r.chasis}
                  rowHeight={52}
                  hideFooter
                  slots={{ noRowsOverlay: NoRowsOverlay }}
                  sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "180px" }}
                  disableRowSelectionOnClick
                />
              </Box>
            </Box>
          ))}
          {hayBajoMinimo && (
            <Typography
              sx={{ mt: 1.4, fontWeight: 700, fontSize: "0.82rem", color: FP.color.danger }}
            >
              Hay precios por debajo del mínimo del chasis
            </Typography>
          )}
        </Box>

        {/* Plan de pago (solo crédito) */}
        {tipofactura === "Credito" && (
          <Box sx={sectionBox}>
            <SectionTitle icon={CalendarTodayOutlinedIcon} color={FP.color.navy}>
              Plan de pago
            </SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Plazos"
                  value={plazos}
                  onChange={(e) => setPlazos(e.target.value)}
                  inputProps={{ min: 1, max: 60 }}
                  helperText="Cuotas mensuales (1 a 60)"
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha primer pago"
                  value={fechaprimerpago}
                  onChange={(e) => setFechaprimerpago(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "center" }}>
                {cuotasPlan.length > 0 && (
                  <Chip
                    label={`${cuotasPlan.length} cuota(s) de ${monedaLabel} ${fmtMoney(
                      cuotasPlan[0].valor
                    )}${inicial > 0 ? ` · financiado ${monedaLabel} ${fmtMoney(financiado)}` : ""}`}
                    sx={{
                      bgcolor: FP.color.surfaceAlt,
                      color: FP.color.textSecondary,
                      fontWeight: 700,
                    }}
                  />
                )}
              </Grid>
              {usaInicial && variasEmpresas && (
                <Grid item xs={12}>
                  <Typography
                    sx={{ fontWeight: 700, fontSize: "0.82rem", color: FP.color.textSecondary }}
                  >
                    Inicial por empresa (cada factura lleva el suyo)
                  </Typography>
                </Grid>
              )}
              {usaInicial &&
                variasEmpresas &&
                planes.map((p) => (
                  <React.Fragment key={p.empresa}>
                    <Grid item xs={12} sm={4} sx={{ display: "flex", alignItems: "center" }}>
                      <Typography sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                        {nombreEmpresa(p.empresa)}
                        <Box
                          component="span"
                          sx={{ display: "block", fontWeight: 500, color: FP.color.textMuted }}
                        >
                          Total {monedaLabel} {fmtMoney(p.total)}
                        </Box>
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Inicial pactado"
                        value={inicialesEmpresa[p.empresa]?.pactado || ""}
                        onChange={(e) =>
                          cambiarInicialEmpresa(p.empresa, "pactado", e.target.value)
                        }
                        placeholder="0.00"
                        error={p.inicial > 0 && p.inicial >= p.total}
                        inputProps={{ style: { textAlign: "right" } }}
                        sx={fpInputSx}
                      />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Cobrado ahora"
                        value={inicialesEmpresa[p.empresa]?.cobrado || ""}
                        onChange={(e) =>
                          cambiarInicialEmpresa(p.empresa, "cobrado", e.target.value)
                        }
                        placeholder="0.00"
                        disabled={p.inicial <= 0}
                        error={p.cobrado > p.inicial}
                        helperText={
                          p.inicial > 0
                            ? `Pendiente: ${monedaLabel} ${fmtMoney(
                                Math.max(0, p.inicial - p.cobrado)
                              )}`
                            : undefined
                        }
                        inputProps={{ style: { textAlign: "right" } }}
                        sx={fpInputSx}
                      />
                    </Grid>
                  </React.Fragment>
                ))}
              {usaInicial && !variasEmpresas && (
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    label="Inicial pactado"
                    value={inicialpactado}
                    onChange={(e) => setInicialpactado(e.target.value)}
                    placeholder="0.00"
                    error={inicial > 0 && inicial >= total}
                    helperText={`${monedaLabel} · se cobra junto a las cuotas`}
                    inputProps={{ style: { textAlign: "right" } }}
                    sx={fpInputSx}
                  />
                </Grid>
              )}
              {usaInicial && !variasEmpresas && (
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    label="Inicial cobrado ahora"
                    value={inicialcobrado}
                    onChange={(e) => setInicialcobrado(e.target.value)}
                    placeholder="0.00"
                    disabled={inicial <= 0}
                    error={cobradoInicial > inicial}
                    helperText={
                      inicial > 0
                        ? `Pendiente: ${monedaLabel} ${fmtMoney(
                            Math.max(0, inicial - cobradoInicial)
                          )}`
                        : "Sin inicial pactado"
                    }
                    inputProps={{ style: { textAlign: "right" } }}
                    sx={fpInputSx}
                  />
                </Grid>
              )}
              {planes.some((p) => p.inicial > 0 && p.cobrado < p.inicial) && (
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Vence el completivo"
                    value={vencimientoinicial}
                    onChange={(e) => setVencimientoinicial(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: hoyMasDias(0) }}
                    error={!!vencimientoinicial && vencimientoinicial < hoyMasDias(0)}
                    helperText="Fecha límite para cobrar lo pendiente del inicial"
                    sx={fpInputSx}
                  />
                </Grid>
              )}
              {cobradoInicial > 0 && (
                <>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      select
                      fullWidth
                      label="Tipo de pago del inicial"
                      value={tipopagoinicial}
                      onChange={(e) => setTipopagoinicial(e.target.value)}
                      sx={fpInputSx}
                    >
                      {(config?.tiposPago || ["Contado", "Transferencias"]).map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth
                      label="Referencia"
                      value={referenciainicial}
                      onChange={(e) => setReferenciainicial(e.target.value)}
                      sx={fpInputSx}
                    />
                  </Grid>
                </>
              )}
              {variasEmpresas &&
                planes
                  .filter((p) => p.cuotas.length > 0)
                  .map((p) => (
                    <Grid item xs={12} md={6} key={p.empresa}>
                      <Typography
                        sx={{ fontWeight: 700, fontSize: "0.82rem", mb: 0.6, color: FP.color.navy }}
                      >
                        Factura de {nombreEmpresa(p.empresa)} ·{" "}
                        {p.cuotas.filter((c) => c.numero !== 0).length} cuota(s) · financiado{" "}
                        {monedaLabel} {fmtMoney(p.financiado)}
                      </Typography>
                      <Box sx={{ bgcolor: "#fff" }}>
                        <DataGrid
                          autoHeight
                          rows={p.cuotas}
                          columns={columnasCuotas}
                          getRowId={(r) => r.numero}
                          rowHeight={40}
                          hideFooter
                          sx={fpDataGridSx}
                          disableRowSelectionOnClick
                        />
                      </Box>
                    </Grid>
                  ))}
              {cuotasPreview.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ bgcolor: "#fff" }}>
                    <DataGrid
                      autoHeight
                      rows={cuotasPreview}
                      columns={columnasCuotas}
                      getRowId={(r) => r.numero}
                      rowHeight={40}
                      hideFooter
                      sx={fpDataGridSx}
                      disableRowSelectionOnClick
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* Guardar */}
        <Box sx={{ ...sectionBox, mb: 0, display: "flex", justifyContent: "flex-end" }}>
          <Button
            startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={guardar}
            disabled={guardando || cargando || restaurando || hayBajoMinimo}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Guardar e imprimir
          </Button>
        </Box>
      </Box>

      {/* Varias empresas: una factura por empresa, se imprimen una a una */}
      <Dialog open={!!emitidas} onClose={() => setEmitidas(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800 }}>Facturas registradas</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary, mb: 1.6 }}>
            Se emitió una factura por cada empresa. Imprima cada una.
          </Typography>
          <Stack spacing={1.2}>
            {(emitidas || []).map((f) => (
              <Stack
                key={f.id}
                direction="row"
                alignItems="center"
                spacing={1.4}
                sx={{
                  border: `1px solid ${FP.color.border}`,
                  borderRadius: 2,
                  p: 1.4,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 800, fontSize: "0.9rem" }}>
                    Factura #{f.id}
                    {f.ncf ? ` · ${f.ncf}` : ""}
                  </Typography>
                  <Typography sx={{ fontSize: "0.78rem", color: FP.color.textMuted }}>
                    {f.empresa != null ? `${nombreEmpresa(f.empresa)} · ` : ""}
                    {f.items.length} chasis · {f.monedaLabel} {fmtMoney(f.total)}
                    {f.reciboinicial ? ` · inicial en recibo #${f.reciboinicial}` : ""}
                  </Typography>
                </Box>
                <Button
                  startIcon={<PrintOutlinedIcon />}
                  onClick={() => imprimirFacturaXmayor(f)}
                  sx={btnOutline(FP.color.navy)}
                >
                  Imprimir
                </Button>
              </Stack>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmitidas(null)} sx={btnSolid(FP.color.navy, FP.color.navySoft)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
